// src/AnnouncementPage.js
import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import './AuthForm.css';
import Sidebar from './Sidebar';
import ProfileMenu from './ProfileMenu';
import MobileHamburger from './MobileHamburger';
import MobileMenu from './MobileMenu';
import './Main.css';
import barangays from './barangays.json';
import { useNavigate } from 'react-router-dom';
import { FaPen } from 'react-icons/fa';
import { FaPlus, FaTrash } from 'react-icons/fa6';
import Preloader from './Preloader'; 

const TARGETS = ['barangay', 'city'];
const STATUSES = ['active', 'archived', 'draft'];

function AnnouncementPage() {
  const [announcements, setAnnouncements] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ 
    title: '', 
    content: '', 
    target_scope: '', 
    target_barangay: '',
    banner_img: '',
    status: 'active'
  });
  const [editId, setEditId] = useState(null);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showBarangayDropdown, setShowBarangayDropdown] = useState(false);
  const [barangaySearch, setBarangaySearch] = useState('');
  const [sidebarNarrow, setSidebarNarrow] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const navigate = useNavigate();
  const itemsPerPage = 10;
  const [page, setPage] = useState(1);
  const [showMobileMenu, setShowMobileMenu] = useState(false);  
  
  useEffect(() => {
    document.title = "Announcements";
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', user.id)
          .single();
        setCurrentUser(profile);
      }
    };
    getCurrentUser();
  }, []);

  useEffect(() => {
    fetchAnnouncements();

    const announcementsChannel = supabase
      .channel('public:announcements')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'announcements' },
        (payload) => {
          console.log('Announcement change received!', payload);
          fetchAnnouncements();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(announcementsChannel);
    };
  }, [statusFilter]);

  async function fetchAnnouncements() {
    setLoading(true);
    try {
      let query = supabase
        .from('announcements')
        .select('*');
      
      if (search) query = query.ilike('title', `%${search}%`);
      if (filter) query = query.eq('target_scope', filter);
      query = query.eq('status', statusFilter);
      
      const { data: announcements, error: announcementsError } = await query.order('created_at', { ascending: false });
      
      if (announcementsError) {
        console.error('Error fetching announcements:', announcementsError);
        setAnnouncements([]);
      } else {
        setAnnouncements(announcements || []);
      }
    } catch (error) {
      console.error('Error in fetchAnnouncements:', error);
      setAnnouncements([]);
    } finally {
      setLoading(false);
    }
  }

  function openForm(announcement = null) {
    if (announcement) {
      setForm({
        title: announcement.title,
        content: announcement.content,
        target_scope: announcement.target_scope,
        target_barangay: announcement.target_barangay || '',
        banner_img: announcement.banner_img || '',
        status: announcement.status
      });
      setEditId(announcement.id);
    } else {
      const isBarangay = currentUser?.role === 'barangay';
      setForm({ 
        title: '', 
        content: '', 
        target_scope: isBarangay ? 'barangay' : '', 
        target_barangay: isBarangay ? currentUser.barangay : '',
        banner_img: '',
        status: 'active'
      });
      setEditId(null);
    }
    setShowForm(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Check if user is authenticated
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) {
        alert('Please log in to create announcements');
        return;
      }

      // Ensure target_scope is lowercase
      const announcementData = {
        ...form,
        target_scope: form.target_scope.toLowerCase()
      };

      let error;
      let announcementId = editId;
      let actionType = 'post announcement';
      if (editId) {
        const { error: updateError } = await supabase
          .from('announcements')
          .update(announcementData)
          .eq('id', editId);
        error = updateError;
        actionType = 'edited announcement';
      } else {
        const { data: insertData, error: insertError } = await supabase
          .from('announcements')
          .insert([announcementData])
          .select();
        error = insertError;
        if (insertData && insertData.length > 0) {
          announcementId = insertData[0].id;
        }
      }

      if (error) throw error;

      // Audit log for post/edit
      const targetType = announcementData.target_scope === 'barangay' ? 'AnnouncementBarangay' : 'AnnouncementCitywide';
      await supabase.from('audit_logs').insert([{
        user_id: currentUser.id,
        action: actionType,
        target_id: announcementId,
        target_type: targetType,
        details: {
          title: announcementData.title,
          target_scope: announcementData.target_scope,
          target_barangay: announcementData.target_barangay || null
        }
      }]);

      setShowForm(false);
      await fetchAnnouncements();
    } catch (error) {
      console.error('Error saving announcement:', error);
      alert(`Failed to save announcement: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }

  async function handleStatusChange(id, newStatus) {
    try {
      // Check if user is authenticated
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) {
        alert('Please log in to update announcements');
        return;
      }

      const { error } = await supabase
        .from('announcements')
        .update({ status: newStatus })
        .eq('id', id);
      
      if (error) throw error;
      await fetchAnnouncements();
    } catch (error) {
      console.error('Error updating status:', error);
      alert(`Failed to update status: ${error.message}`);
    }
  }

  async function handleDelete(id) {
    try {
      // Check if user is authenticated
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) {
        alert('Please log in to delete announcements');
        return;
      }

      // Get announcement for audit log
      const { data: annData } = await supabase
        .from('announcements')
        .select('*')
        .eq('id', id)
        .single();

      const { error } = await supabase
        .from('announcements')
        .update({ status: 'archived' })
        .eq('id', id);
      
      if (error) throw error;

      // Audit log for archive
      if (annData) {
        const targetType = annData.target_scope === 'barangay' ? 'AnnouncementBarangay' : 'AnnouncementCitywide';
        await supabase.from('audit_logs').insert([{
          user_id: currentUser.id,
          action: 'archived announcement',
          target_id: id,
          target_type: targetType,
          details: {
            title: annData.title,
            target_scope: annData.target_scope,
            target_barangay: annData.target_barangay || null
          }
        }]);
      }
      await fetchAnnouncements();
    } catch (error) {
      console.error('Error archiving announcement:', error);
      alert(`Failed to archive announcement: ${error.message}`);
    }
  }

  async function handleDeleteSelected() {
    try {
      // Check if user is authenticated
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) {
        alert('Please log in to delete announcements');
        return;
      }

      // Get announcements for audit log
      const { data: anns } = await supabase
        .from('announcements')
        .select('*')
        .in('id', selected);

      const { error } = await supabase
        .from('announcements')
        .update({ status: 'archived' })
        .in('id', selected);
      
      if (error) throw error;
      setSelected([]);

      // Audit log for each archived announcement
      if (anns && anns.length > 0) {
        for (const ann of anns) {
          const targetType = ann.target_scope === 'barangay' ? 'AnnouncementBarangay' : 'AnnouncementCitywide';
          await supabase.from('audit_logs').insert([{
            user_id: currentUser.id,
            action: 'archived announcement',
            target_id: ann.id,
            target_type: targetType,
            details: {
              title: ann.title,
              target_scope: ann.target_scope,
              target_barangay: ann.target_barangay || null
            }
          }]);
        }
      }
      await fetchAnnouncements();
    } catch (error) {
      console.error('Error archiving selected announcements:', error);
      alert(`Failed to archive selected announcements: ${error.message}`);
    }
  }

  async function handleImageUpload(event) {
    try {
      setUploading(true);
      
      if (!event.target.files || event.target.files.length === 0) {
        throw new Error('You must select an image to upload.');
      }

      const file = event.target.files[0];
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `announcement-banners/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('announcement-banner')
        .upload(filePath, file);

      if (uploadError) {
        throw uploadError;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('announcement-banner')
        .getPublicUrl(filePath);

      setForm({ ...form, banner_img: publicUrl });
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error uploading image: ' + error.message);
    } finally {
      setUploading(false);
    }
  }

  const filteredBarangays = barangays.filter(barangay => 
    barangay.name.toLowerCase().includes(barangaySearch.toLowerCase())
  );

  const handleBarangaySelect = (barangay) => {
    setForm({ ...form, target_barangay: barangay.name });
    setBarangaySearch(barangay.name);
    setShowBarangayDropdown(false);
  };

  // Case-insensitive in-memory filtering
  const filteredAnnouncements = announcements.filter(a => {
    const matchesSearch = !search || (a.title && a.title.toLowerCase().includes(search.toLowerCase()));
    const matchesTarget = !filter || (a.target_scope && a.target_scope.toLowerCase() === filter.toLowerCase());
    const matchesStatus = !statusFilter || (a.status && a.status.toLowerCase() === statusFilter.toLowerCase());
    return matchesSearch && matchesTarget && matchesStatus;
  });

  const totalPages = Math.ceil(filteredAnnouncements.length / itemsPerPage);
  const paginatedAnnouncements = filteredAnnouncements.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  if (loading) return <Preloader text="Loading Announcements..." />;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f8f8' }}>
      <Sidebar onNarrowChange={setSidebarNarrow} />
      <div 
        className="announcement-main-content"
        style={{ 
          flex: 1, 
          padding: 32, 
          overflowX: 'auto', 
          marginLeft: sidebarNarrow ? 0 : 0, 
          transition: 'margin-left 0.2s',
          width: '100%'
        }}
      >
        <div className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {/* Mobile Hamburger Menu */}
            <MobileHamburger
              isOpen={showMobileMenu}
              onToggle={() => setShowMobileMenu(prev => !prev)}
            />
            <div className="title-text">Announcements</div>
          </div>
          <ProfileMenu />
        </div>

        {/* Mobile Dropdown Menu */}
        <MobileMenu 
          isOpen={showMobileMenu} 
          onClose={() => setShowMobileMenu(false)} 
        />
        {/* Search & Actions */}
        <div className="search-actions-row">
          <div className="search-filters">
            <input
              type="text"
              placeholder="Search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="search-input"
            />
            <select
              value={filter}
              onChange={e => setFilter(e.target.value)}
              className="type-filter"
            >
              <option value="">All Target</option>
              {TARGETS.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
            </select>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="type-filter"
            >
              {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
            </select>
          </div>
          {currentUser && (
            <div className="action-buttons">
              <button onClick={() => openForm()} className="create-button">
                <FaPlus /> Create Announcement
              </button>
              {(currentUser.role === 'admin' || currentUser.role === 'super admin') && (
                <button onClick={handleDeleteSelected} disabled={selected.length === 0} className="archive-button">
                  <FaTrash /> Archive Selected
                </button>
              )}
            </div>
          )}
        </div>
        {/* Table */}
        <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px #0001', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left' }}>
                <th style={{ padding: 12 }}><input type="checkbox" checked={selected.length === announcements.length && announcements.length > 0} onChange={e => setSelected(e.target.checked ? announcements.map(a => a.id) : [])} /></th>
                <th style={{ padding: 12 }}>Banner</th>
                <th style={{ padding: 12, maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Title</th>
                <th style={{ padding: 12 }}>Date Uploaded</th>
                <th style={{ padding: 12, maxWidth: 120, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Target</th>
                <th style={{ padding: 12 }}>Status</th>
                <th style={{ padding: 12 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedAnnouncements.length > 0 ? (
                paginatedAnnouncements.map(a => (
                  <tr key={a.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: 12 }}><input type="checkbox" checked={selected.includes(a.id)} onChange={e => setSelected(e.target.checked ? [...selected, a.id] : selected.filter(id => id !== a.id))} /></td>
                    <td 
                      style={{ padding: 12, cursor: 'pointer' }} 
                      onClick={() => navigate(`/announcements/${a.id}`)}
                    >
                      {a.banner_img && (
                        <img 
                          src={a.banner_img} 
                          alt="Banner" 
                          style={{ width: 100, height: 60, objectFit: 'cover', borderRadius: 4 }}
                        />
                      )}
                    </td>
                    <td 
                      style={{ padding: 12, cursor: 'pointer', maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} 
                      onClick={() => navigate(`/announcements/${a.id}`)}
                    >
                      <div style={{ fontWeight: 'bold', maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.title}</div>
                      <div style={{ color: '#444', fontSize: 14, maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.content?.slice(0, 40)}{a.content?.length > 40 ? '...' : ''}</div>
                    </td>
                    <td 
                      style={{ padding: 12, cursor: 'pointer' }} 
                      onClick={() => navigate(`/announcements/${a.id}`)}
                    >{new Date(a.created_at).toLocaleDateString()}</td>
                    <td 
                      style={{ padding: 12, cursor: 'pointer', maxWidth: 120, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} 
                      onClick={() => navigate(`/announcements/${a.id}`)}
                    >{a.target_scope === 'barangay' ? a.target_barangay : 'City'}</td>
                    <td style={{ padding: 12 }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '4px 12px',
                          borderRadius: 12,
                          fontWeight: 600,
                          fontSize: 14,
                          background:
                            a.status === 'active' ? '#e6f4ea' :
                            a.status === 'draft' ? '#fff9e6' :
                            '#f0f0f0',
                          color:
                            a.status === 'active' ? '#19c37d' :
                            a.status === 'draft' ? '#e6a700' :
                            '#888',
                          border:
                            a.status === 'active' ? '1px solid #19c37d33' :
                            a.status === 'draft' ? '1px solid #e6a70033' :
                            '1px solid #ccc',
                          minWidth: 70,
                          textAlign: 'center'
                        }}
                      >
                        {a.status.charAt(0).toUpperCase() + a.status.slice(1)}
                      </span>
                    </td>
                    <td style={{ padding: 12 }}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {(currentUser?.role === 'admin' || currentUser?.role === 'super admin' || currentUser?.role === 'barangay') && (
                          <button onClick={() => openForm(a)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20 }}>
                            <FaPen />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: 32, color: '#888' }}>No announcements found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 24, gap: 8 }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ background: '#eee', border: 'none', borderRadius: 4, padding: '4px 12px', cursor: page === 1 ? 'not-allowed' : 'pointer' }}>Previous</button>
            <div style={{ display: 'flex', gap: 4 }}>
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i + 1}
                  onClick={() => setPage(i + 1)}
                  style={{
                    padding: '4px 12px',
                    borderRadius: 4,
                    border: '1px solid #ccc',
                    cursor: 'pointer',
                    background: page === i + 1 ? '#08b1c7' : '#fff',
                    color: page === i + 1 ? '#fff' : '#333',
                    fontWeight: page === i + 1 ? 'bold' : 'normal'
                  }}
                >
                  {i + 1}
                </button>
              ))}
            </div>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ background: '#eee', border: 'none', borderRadius: 4, padding: '4px 12px', cursor: page === totalPages ? 'not-allowed' : 'pointer' }}>Next</button>
          </div>
        )}
        {showForm && (
          <div style={{
            position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
            background: '#0008', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}>
            <div style={{
              background: '#fff',
              borderRadius: 12,
              padding: 32,
              minWidth: 320,
              maxWidth: '90vw',
              width: 600,
              height: 600,
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 4px 32px rgba(8, 177, 199, 0.18)'
            }}>
              {/* Modal Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexShrink: 0 }}>
                <h2 style={{ margin: 0, fontSize: 24, fontWeight: 'bold' }}>
                  {editId ? 'Edit Announcement' : 'Create New Announcement'}
                </h2>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: 28,
                    cursor: 'pointer',
                    color: '#888',
                    marginLeft: 8
                  }}
                >
                  ×
                </button>
              </div>
              {/* Scrollable Form Fields */}
              <form
                onSubmit={handleSubmit}
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 16
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={{ marginBottom: 4 }}>Title</label>
                  <input
                    type="text"
                    placeholder="Enter a title"
                    value={form.title}
                    onChange={e => setForm({ ...form, title: e.target.value })}
                    required
                    style={{ padding: 12, borderRadius: 6, border: '1px solid #ccc', fontSize: 16 }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={{ marginBottom: 4 }}>Content</label>
                  <textarea
                    placeholder="Enter content"
                    value={form.content}
                    onChange={e => setForm({ ...form, content: e.target.value })}
                    required
                    style={{ padding: 12, borderRadius: 6, border: '1px solid #ccc', minHeight: 100, fontSize: 16 }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={{ marginBottom: 4 }}>Target Scope</label>
                  <select
                    value={form.target_scope}
                    onChange={e => setForm({ ...form, target_scope: e.target.value })}
                    required
                    disabled={currentUser?.role === 'barangay'}
                    style={{ padding: 12, borderRadius: 6, border: '1px solid #ccc', fontSize: 16 }}
                  >
                    <option value="">Select Target</option>
                    {TARGETS.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                  </select>
                </div>
                {form.target_scope === 'barangay' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, position: 'relative' }}>
                    <label style={{marginBottom: 4 }}>Barangay</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        placeholder="Search barangay"
                        value={form.target_barangay && !showBarangayDropdown ? form.target_barangay : barangaySearch}
                        onChange={e => {
                          setBarangaySearch(e.target.value);
                          setForm({ ...form, target_barangay: '' });
                          setShowBarangayDropdown(true);
                        }}
                        onFocus={() => setShowBarangayDropdown(true)}
                        required
                        disabled={currentUser?.role === 'barangay'}
                        style={{ padding: 12, borderRadius: 6, border: '1px solid #ccc', width: '100%', fontSize: 16 }}
                      />
                      {showBarangayDropdown && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          maxHeight: '200px',
                          overflowY: 'auto',
                          background: 'white',
                          border: '1px solid #ccc',
                          borderRadius: '0 0 6px 6px',
                          zIndex: 1000,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                          {filteredBarangays.map(barangay => (
                            <div
                              key={barangay.number}
                              onClick={() => handleBarangaySelect(barangay)}
                              style={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                fontSize: 16
                              }}
                            >
                              {barangay.name}
                            </div>
                          ))}
                          {filteredBarangays.length === 0 && (
                            <div style={{ padding: '8px 12px', color: '#666', fontSize: 16 }}>
                              No barangays found
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div>
                  <label
                    htmlFor="announcement-banner-upload"
                    style={{
                      display: 'inline-block',
                      padding: '10px 20px',
                      background: '#fff',
                      color: '#19c37d',
                      border: '1px solid #19c37d',
                      borderRadius: 8,
                      cursor: 'pointer',
                      marginBottom: 8,
                      marginTop: 4
                    }}
                  >
                    Upload Banner Image
                  </label>
                  <input
                    id="announcement-banner-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                  />
                  {form.banner_img && (
                    <img
                      src={form.banner_img}
                      alt="Banner preview"
                      style={{ width: '100%', maxWidth: '100%', height: 180, objectFit: 'cover', borderRadius: 6, marginTop: 8 }}
                    />
                  )}
                  {uploading && <div style={{ color: '#666', fontSize: 16 }}>Uploading...</div>}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={{ marginBottom: 4 }}>Status</label>
                  <select
                    value={form.status || 'active'}
                    onChange={e => setForm({ ...form, status: e.target.value })}
                    required
                    style={{ padding: 12, borderRadius: 6, border: '1px solid #ccc', fontSize: 16 }}
                  >
                    <option value="active">Active</option>
                    <option value="draft">Draft</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              </form>
              {/* Modal Footer (fixed at bottom, outside scrollable area) */}
              <div style={{
                display: 'flex',
                gap: 16,
                justifyContent: 'flex-end',
                marginTop: 24,
                flexShrink: 0,
                background: '#fff'
              }}>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  style={{
                    padding: '12px 24px',
                    borderRadius: 8,
                    border: '1px solid #ccc',
                    background: '#fff',
                    cursor: 'pointer',
                    fontSize: 16
                  }}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  form="announcement-form"
                  style={{
                    padding: '12px 24px',
                    borderRadius: 8,
                    border: 'none',
                    background: '#19c37d',
                    color: '#fff',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: 16,
                    fontWeight: 'bold',
                    opacity: loading ? 0.7 : 1
                  }}
                  disabled={loading}
                  onClick={() => document.querySelector('form').requestSubmit()}
                >
                  {loading ? (editId ? 'Saving...' : 'Creating...') : (editId ? 'Save' : 'Create')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AnnouncementPage;